import { useState } from 'react';
import JSZip from 'jszip';
import { useToast } from '@/hooks/use-toast';

export interface ExportOptions {
  format: 'zip' | 'individual';
  namingTemplate: string; // e.g., "{name}_{index}_{tool}_{date}"
  folderStructure: 'flat' | 'by-tool' | 'by-date';
  includeMetadata: boolean;
  includeReport: boolean;
}

export interface ImageForExport {
  id: string;
  name: string;
  blob: Blob;
  dataUrl: string;
  tool?: string;
  metadata?: Record<string, any>;
}

export const useEnhancedExport = () => {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const formatFileName = (
    template: string,
    image: ImageForExport,
    index: number
  ): string => {
    const date = new Date().toISOString().split('T')[0];
    const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
    
    return template
      .replace('{name}', image.name.replace(/\.[^/.]+$/, ''))
      .replace('{index}', String(index + 1).padStart(3, '0'))
      .replace('{tool}', image.tool || 'processed')
      .replace('{date}', date)
      .replace('{time}', time);
  };

  const generateMetadataFile = (images: ImageForExport[]): string => {
    const metadata = images.map((img, index) => ({
      index: index + 1,
      name: img.name,
      tool: img.tool,
      size: img.blob.size,
      type: img.blob.type,
      ...img.metadata,
    }));

    return JSON.stringify(metadata, null, 2);
  };

  const generateReportPDF = (images: ImageForExport[]): string => {
    // Simple HTML report (in production, use a proper PDF library)
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>Image Processing Report</h1>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <p>Total Images: ${images.length}</p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Tool</th>
                <th>Size</th>
              </tr>
            </thead>
            <tbody>
              ${images.map((img, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${img.name}</td>
                  <td>${img.tool || 'N/A'}</td>
                  <td>${(img.blob.size / 1024).toFixed(2)} KB</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    return html;
  };

  const exportImages = async (
    images: ImageForExport[],
    options: ExportOptions
  ) => {
    try {
      setExporting(true);
      setProgress(0);

      if (options.format === 'individual') {
        // Download each image individually
        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          const fileName = formatFileName(options.namingTemplate, img, i);
          const extension = img.blob.type.split('/')[1] || 'png';
          
          const link = document.createElement('a');
          link.href = img.dataUrl;
          link.download = `${fileName}.${extension}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setProgress(((i + 1) / images.length) * 100);
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
        }
      } else {
        // Create ZIP archive
        const zip = new JSZip();

        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          const fileName = formatFileName(options.namingTemplate, img, i);
          const extension = img.blob.type.split('/')[1] || 'png';
          
          let folderPath = '';
          if (options.folderStructure === 'by-tool' && img.tool) {
            folderPath = `${img.tool}/`;
          } else if (options.folderStructure === 'by-date') {
            const date = new Date().toISOString().split('T')[0];
            folderPath = `${date}/`;
          }

          zip.file(`${folderPath}${fileName}.${extension}`, img.blob);
          setProgress(((i + 1) / images.length) * 80);
        }

        // Add metadata file
        if (options.includeMetadata) {
          const metadataContent = generateMetadataFile(images);
          zip.file('metadata.json', metadataContent);
        }

        // Add processing report
        if (options.includeReport) {
          const reportContent = generateReportPDF(images);
          zip.file('report.html', reportContent);
        }

        setProgress(90);

        // Generate and download ZIP
        const blob = await zip.generateAsync({ 
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: { level: 6 }
        });

        setProgress(95);

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `processed_images_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }

      setProgress(100);
      
      toast({
        title: 'Success',
        description: `Exported ${images.length} image${images.length > 1 ? 's' : ''} successfully`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Error',
        description: 'Failed to export images',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
      setProgress(0);
    }
  };

  return {
    exporting,
    progress,
    exportImages,
  };
};